/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import isEmpty from 'lodash.isempty';
import {viewAdminDevice, viewDeviceById} from '@ion/applications/src/pages/devices/state';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {BulkActionEntity} from '@ion/core/src/types/entities';
import {changeURLPath} from '@ion/core/src/utils/BrowserUtils';
import {safeParseNumber} from '@ion/core/src/utils/NumberUtil';
import {useLoadDevices} from '@ion/query/src/hooks/useLoadDevices';
import {useAuthenticatedUser} from '../pages/admin/users/useAuthenticatedUser';
import {toggleUserPrompt} from '../store/actions/user-prompt';
import {useUrl} from './useUrl';

// Enum defined for the identifiers which supports device shareable link.
enum Identifiers {
  ID = 'id',
  ESN = 'esn',
  SERIALNUMBER = 'serial',
  IMEI = 'imei',
  IMEI2 = 'imei2',
  MOBILEID = 'mobile'
}

// Enum defined for the identifiers terms to show in error message.
enum IdentifierTerms {
  ID = 'ID',
  ESN = 'ESN',
  SERIAL = 'SERIAL NUMBER',
  IMEI = 'IMEI',
  IMEI2 = 'imei2',
  MOBILE = 'MOBILE ID'
}

// Object with identifiers as keys and smartSearch API payload supported identifier terms as values.
const idMap = {
  [Identifiers.ID]           : 'id',
  [Identifiers.ESN]          : 'esn',
  [Identifiers.SERIALNUMBER] : 'serialNumber',
  [Identifiers.IMEI]         : 'imei',
  [Identifiers.IMEI2]        : 'imei2',
  [Identifiers.MOBILEID]     : 'userDefinedMobileId'
};

// Array of identifiers which supports fetching device details using shareable link.
export const ids = [
  Identifiers.ID,
  Identifiers.ESN,
  Identifiers.SERIALNUMBER,
  Identifiers.IMEI,
  Identifiers.IMEI2,
  Identifiers.MOBILEID
];

/**
 * useDeviceDeepLink hook used to open device dialog view based on
 * url params related to device id
 *
 * ```typescript
 *
 * import {useDeviceDeepLink} from '@ion/applications/src/hooks/';
 *
 * const deviceId = useDeviceDeepLink();
 * ```
 */
export const useDeviceDeepLink = () => {
  const dispatch      = useDispatch();
  const {account}     = useAuthenticatedUser();
  const {loadDevices} = useLoadDevices();
  const {
    hasQueryParam,
    getQueryParams,
    getPath
  }                   = useUrl();
  const params        = getQueryParams();
  const identifier    = !isEmpty(params) ? ids.filter(id => hasQueryParam(id))?.[0] || '' : '';
  const idValue       = params[identifier];

  useEffect(() => {
    if (!isEmpty(idValue)) {
      getDeviceId(idValue);
    } else {
      dispatch(viewAdminDevice(null, false));
    }
  }, [idValue]);

  const showError = useCallback((notFound: boolean) => {
    changeURLPath(getPath());
    dispatch(toggleUserPrompt({
      autoHide    : false,
      actionLabel : translate('common:dismiss'),
      message     : notFound ? translate('admin-devices:viewDevice.notFound', {identifier: IdentifierTerms[identifier.toUpperCase()], idValue}) : translate('admin-devices:viewDevice.deviceError')
    }));
  }, [identifier, idValue]);

  const getDeviceId = useCallback((idValue) => {
    try {
      loadDevices({
        hierLevel      : true,
        pgSize         : 100,
        accountId      : entityId(account),
        identifiers    : [safeParseNumber(idValue)],
        omitExtensions : true,
        entity         : BulkActionEntity.DEVICES,
        identifierType : idMap[identifier]
      }, {
        onSuccess : (devices) => {
          if (devices?.length) {
            const {id: deviceId} = devices[0]?.device;
            dispatch(viewDeviceById(deviceId));
          } else {
            showError(true);
          }
        },
        onError : () => {
          showError(false);
        }
      });
    } catch (err) {
      showError(false);
    }
  }, [account, identifier]);

  return idValue;
};

export default useDeviceDeepLink;
